import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMalaysiaDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
  } catch (e) {
    return value;
  }
};

const formatMalaysiaTimeOnly = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
  } catch (e) {
    return value;
  }
};

const AdminAttendanceSection = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('today'); // 'today' or 'monthly'
  const [yearMonth, setYearMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [exporting, setExporting] = useState(false);

  const fetchTodayRecords = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/attendance/today/all`, { withCredentials: true });
      setRecords(data || []);
    } catch (err) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyRecords = async (year, month) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/attendance/records?user_id=all&year=${year}&month=${month}`, { withCredentials: true });
      setRecords(data || []);
    } catch (err) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/api/attendance/monthly/export?year=${yearMonth.year}&month=${yearMonth.month}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance-${yearMonth.year}-${String(yearMonth.month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attendance CSV downloaded');
    } catch (err) {
      toast.error('Failed to download attendance');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'today') {
      fetchTodayRecords();
    } else {
      fetchMonthlyRecords(yearMonth.year, yearMonth.month);
    }
  }, [viewMode, yearMonth]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6">
      {/* View Mode Selector */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-medium">View Mode</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-4 flex-wrap">
          <Button
            variant={viewMode === 'today' ? 'default' : 'outline'}
            onClick={() => setViewMode('today')}
          >
            Today's Attendance
          </Button>
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'outline'}
            onClick={() => setViewMode('monthly')}
          >
            Monthly View
          </Button>
        </CardContent>
      </Card>

      {viewMode === 'today' && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl font-medium tracking-tight">Teachers' Attendance (Today)</CardTitle>
            <CardDescription>View check-in and check-out times for all teachers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Time In</TableHead>
                    <TableHead>Time Out</TableHead>
                    <TableHead>Late</TableHead>
                    <TableHead>Overtime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500">No attendance records for today</TableCell>
                    </TableRow>
                  ) : (
                    records.map((r) => (
                      <TableRow key={r.user_id || r.id}>
                        <TableCell className="font-medium">{r.user_name || r.user_id || 'Unknown'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{r.time_in ? formatMalaysiaDateTime(r.time_in) : '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{r.time_out ? formatMalaysiaDateTime(r.time_out) : '—'}</TableCell>
                        <TableCell>{r.is_late ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{r.is_overtime ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {viewMode === 'monthly' && (
        <>
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Select Month & Year</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-4 flex-wrap items-end">
              <div className="flex gap-2 items-end">
                <div>
                  <label className="block text-sm font-medium mb-2">Year</label>
                  <Select value={String(yearMonth.year)} onValueChange={(v) => setYearMonth({ ...yearMonth, year: parseInt(v) })}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Month</label>
                  <Select value={String(yearMonth.month)} onValueChange={(v) => setYearMonth({ ...yearMonth, month: parseInt(v) })}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={handleDownloadCSV}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Downloading...' : 'Download CSV'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-xl font-medium tracking-tight">
                Attendance Records ({yearMonth.year}-{String(yearMonth.month).padStart(2, '0')})
              </CardTitle>
              <CardDescription>All check-in and check-out records for this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time In</TableHead>
                      <TableHead>Time Out</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Late</TableHead>
                      <TableHead>Overtime</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500">No attendance records for this month</TableCell>
                      </TableRow>
                    ) : (
                      records.map((r, idx) => (
                        <TableRow key={`${r.user_id}-${r.date}-${idx}`}>
                          <TableCell className="font-medium text-sm">{r.user_name || r.user_id || 'Unknown'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{r.date || '—'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{r.time_in ? formatMalaysiaTimeOnly(r.time_in) : '—'}</TableCell>
                          <TableCell className="text-sm text-slate-600">{r.time_out ? formatMalaysiaTimeOnly(r.time_out) : '—'}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {r.duration_seconds ? `${Math.floor(r.duration_seconds / 3600)}h ${Math.floor((r.duration_seconds % 3600) / 60)}m` : '—'}
                          </TableCell>
                          <TableCell className="text-sm">{r.is_late ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="text-sm">{r.is_overtime ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminAttendanceSection;
